import { parse, stringify } from 'yaml';
import { AutomationAction } from './ast/AutomationAction.js';
import { detectMediaSourceType } from './mediaPlayerAction.js';
import {
  OBJECT_BACKGROUND_COLOR_REFERENCE,
  objectBackgroundColorToRgb,
} from './objectBackgroundColor.js';

function splitActionName(name) {
  const value = String(name ?? '');
  const dot = value.indexOf('.');
  return dot < 0
    ? { provider: '', service: value }
    : { provider: value.slice(0, dot), service: value.slice(dot + 1) };
}

function actionName(action) {
  const service = String(action.service ?? '');
  if (service.includes('.')) return service;
  return [action.provider, action.service].filter(Boolean).join('.');
}

function parameterValue(action, parameter, object) {
  if (action.provider === 'light' && action.service === 'turn_on'
      && parameter.key === 'rgb_color'
      && parameter.source !== 'literal'
      && parameter.value?.kind === 'literal'
      && parameter.value.value === OBJECT_BACKGROUND_COLOR_REFERENCE) {
    return objectBackgroundColorToRgb(object) ?? parameter.value.toString();
  }
  if (parameter.value?.kind === 'list') return [...parameter.value.items];
  if (parameter.value?.kind === 'object') return clone(parameter.value.value);
  if (parameter.value?.kind === 'literal') return parameter.value.value;
  if (parameter.source === 'event' && parameter.value?.kind === 'reference') {
    return `{{ ${parameter.value.name} }}`;
  }
  return parameter.value.toString();
}

function clone(value) {
  if (Array.isArray(value)) return value.map(clone);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, clone(item)]));
  }
  return value;
}

function cleanMediaPlaybackData(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  return Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== null && value !== undefined && value !== '')
  );
}

function mediaPlaybackData(action) {
  const data = Object.fromEntries(
    (action.parameters ?? [])
      .filter(parameter => parameter.key && parameter.key !== 'entity_id')
      .map(parameter => [parameter.key, parameter.value?.toString() ?? ''])
  );
  return cleanMediaPlaybackData(data);
}

function hasConcreteEntityId(value) {
  if (Array.isArray(value)) {
    return value.some(item => hasConcreteEntityId(item));
  }
  return typeof value === 'string'
    && value.trim() !== ''
    && !value.includes('{{');
}

/** Convert an AutomationAction to Home Assistant's modern action shape. */
export function toHomeAssistantAction(action, { object = null } = {}) {
  const result = { ...clone(action.extraFields), action: actionName(action) };
  let target = clone(action.target);
  const targetContract = action.editorMetadata?.serviceTarget;
  const targetAllowed = !(targetContract?.known === true && targetContract.supportsAny !== true);
  const parameterTarget = targetAllowed
    ? action.parameters?.find(p => p.key === 'entity_id')?.value
    : null;
  if (!targetAllowed) target = null;
  const targetEntityId = target?.entity_id;
  const hasConcreteTarget = hasConcreteEntityId(targetEntityId);
  const deprecatedTemplateTarget = target?.source === 'template' && !hasConcreteTarget;

  // A selected entity always wins. A missing target must remain missing so
  // targetless services such as ESPHome actions are exported unchanged.
  if (deprecatedTemplateTarget) {
    // Do not turn stale Designer metadata into an entity or into the ordinary
    // implicit target fallback. The short-lived Template-source target is unsupported;
    // the user must select a concrete entity.
    target = null;
  } else if (!hasConcreteTarget && parameterTarget) {
    target = {
      entity_id: parameterTarget.kind === 'list'
        ? [...parameterTarget.items]
        : parameterTarget.toString(),
    };
  }

  if (target && action.provider === 'media_player' && action.service === 'play_media' &&
      (target.entity_id === '' || target.entity_id == null)) {
    delete target.entity_id;
  }
  if (target && Object.keys(target).length > 0) result.target = target;

  if (action.data != null) {
    const data = action.provider === 'media_player' && action.service === 'play_media'
      ? cleanMediaPlaybackData(action.data)
      : clone(action.data);
    // Dynamic service fields and user-added parameters share the generic AST
    // parameter list. Merge them over legacy structured data so both editing
    // paths remain exportable when an imported action already had `data`.
    for (const parameter of action.parameters ?? []) {
      if (parameter.key && parameter.key !== 'entity_id') {
        data[parameter.key] = parameterValue(action, parameter, object);
      }
    }
    if (action.provider === 'media_player' && action.service === 'play_media' && !data.media) {
      if (!data.media_content_type && data.media_content_id) data.media_content_type = 'music';
    }
    if (Object.keys(data).length > 0) result.data = data;
  } else {
    const params = (action.parameters ?? []).filter(p => p.key !== 'entity_id');
    if (params.length > 0) {
      result.data = action.provider === 'media_player' && action.service === 'play_media'
        ? mediaPlaybackData(action)
        : Object.fromEntries(params.map(p => [p.key, parameterValue(action, p, object)]));
      if (action.provider === 'media_player' && action.service === 'play_media' &&
          result.data.media_content_id && !result.data.media_content_type) {
        result.data.media_content_type = 'music';
      }
      if (Object.keys(result.data).length === 0) delete result.data;
    }
  }
  return result;
}

/** Parse either modern `action:` or legacy `service:` Home Assistant YAML/JSON. */
export function parseHomeAssistantAction(source) {
  const parsed = typeof source === 'string' ? parse(source) : source;
  const value = Array.isArray(parsed) ? parsed[0] : parsed;
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Expected a Home Assistant action object.');
  }

  const name = value.action ?? value.service;
  if (typeof name !== 'string' || !name.trim()) {
    throw new Error('Action must contain an action: or service: name.');
  }
  if (value.target != null && (typeof value.target !== 'object' || Array.isArray(value.target))) {
    throw new Error('target must be an object.');
  }
  if (value.data != null && (typeof value.data !== 'object' || Array.isArray(value.data))) {
    throw new Error('data must be an object.');
  }

  const action = new AutomationAction({
    type: 'call_service',
    ...splitActionName(name),
    target: clone(value.target ?? null),
    data: clone(value.data ?? null),
    extraFields: Object.fromEntries(
      Object.entries(value).filter(([key]) => !['action', 'service', 'target', 'data'].includes(key))
    ),
  });
  if (action.provider === 'media_player' && action.service === 'play_media' && !action.data?.media) {
    const media = action.data ?? {};
    action.editorMetadata = {
      type: 'media_player',
      operation: 'play_media',
      sourceType: detectMediaSourceType(media.media_content_id),
    };
  } else if (action.provider === 'media_player' && action.service === 'play_media') {
    action.editorMetadata = {
      type: 'media_player',
      operation: 'play_media',
      sourceType: detectMediaSourceType(action.data.media?.media_content_id),
    };
  }
  return action;
}

export function stringifyHomeAssistantAction(action) {
  return stringify(toHomeAssistantAction(action), { lineWidth: 0 }).trimEnd();
}

export function parseStructuredData(source) {
  if (!String(source).trim()) return {};
  const value = parse(source);
  if (value == null) return {};
  if (typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Data must be a YAML/JSON object.');
  }
  return value;
}

export function stringifyStructuredData(data) {
  return stringify(data ?? {}, { lineWidth: 0 }).trimEnd();
}
